import axios from "axios";

/** Admin-scoped API client. Separate from the member `api` in `lib/api.js` so that requests to
 * /admin/* endpoints don't leak into the member axios instance and its request interceptors. */
export const adminApi = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://localhost:8000",
  withCredentials: true,
});

export async function adminLogin(email, password) {
  const { data } = await adminApi.post("/admin/login", { email, password });
  return data;
}

export async function adminLogout() {
  await adminApi.post("/admin/logout");
}

export async function fetchCurrentAdmin() {
  const { data } = await adminApi.get("/admin/me");
  return data;
}

export async function fetchAdminStats() {
  const { data } = await adminApi.get("/admin/stats");
  return data;
}

// Enrolments
export async function listEnrolments() {
  const { data } = await adminApi.get("/admin/enrolments");
  return data;
}
export async function createEnrolment(payload) {
  const { data } = await adminApi.post("/admin/enrolments", payload);
  return data;
}
export async function updateEnrolment(id, payload) {
  const { data } = await adminApi.patch(`/admin/enrolments/${id}`, payload);
  return data;
}
export async function deleteEnrolment(id) {
  await adminApi.delete(`/admin/enrolments/${id}`);
}
export async function bulkDeleteEnrolments(ids) {
  const { data } = await adminApi.post("/admin/enrolments/bulk-delete", { ids });
  return data;  // { deleted, requested }
}
export async function bulkEnrolCsv(file, { cohort, startDate, endDate } = {}) {
  const form = new FormData();
  form.append("file", file);
  if (cohort) form.append("cohort", cohort);
  if (startDate) form.append("start_date", startDate);
  if (endDate) form.append("end_date", endDate);
  const { data } = await adminApi.post("/admin/enrolments/bulk-csv", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}
export async function listCohorts() {
  const { data } = await adminApi.get("/admin/cohorts");
  return data;
}

// Members
export async function listMembers() {
  const { data } = await adminApi.get("/admin/members");
  return data;
}
export async function getMemberDetail(id) {
  const { data } = await adminApi.get(`/admin/members/${id}`);
  return data;
}
export async function updateMember(id, payload) {
  const { data } = await adminApi.patch(`/admin/members/${id}`, payload);
  return data;
}
export async function deleteMember(id) {
  await adminApi.delete(`/admin/members/${id}`);
}
export async function bulkDeleteMembers(memberIds) {
  const { data } = await adminApi.post("/admin/members/bulk-delete", { member_ids: memberIds });
  return data;  // { deleted: number, requested: number }
}

// CMS Pages (Admin)
export async function listCmsPages() {
  const { data } = await adminApi.get("/admin/pages");
  return data;
}
export async function fetchCmsPage(slug) {
  const { data } = await adminApi.get(`/admin/pages/${slug}`);
  return data;
}
export async function saveCmsPage(slug, payload) {
  const { data } = await adminApi.put(`/admin/pages/${slug}`, payload);
  return data;
}

// Admins
export async function listAdmins() {
  const { data } = await adminApi.get("/admin/admins");
  return data;
}
export async function createAdmin(payload) {
  const { data } = await adminApi.post("/admin/admins", payload);
  return data;
}
export async function updateAdmin(id, payload) {
  const { data } = await adminApi.patch(`/admin/admins/${id}`, payload);
  return data;
}
export async function deleteAdmin(id) {
  await adminApi.delete(`/admin/admins/${id}`);
}

// Master settings (integrations)
export async function fetchSettingsCatalogue() {
  const { data } = await adminApi.get("/admin/settings/catalogue");
  return data.providers;
}
export async function listSettings() {
  const { data } = await adminApi.get("/admin/settings");
  return data.settings;
}
export async function upsertSetting(provider, payload) {
  const { data } = await adminApi.put(`/admin/settings/${provider}`, payload);
  return data;
}
export async function testSetting(provider, target = "") {
  const { data } = await adminApi.post(`/admin/settings/${provider}/test`, { target });
  return data;
}
export async function deleteSetting(provider) {
  await adminApi.delete(`/admin/settings/${provider}`);
}

// WATI templates (approved WhatsApp templates)
export async function refreshWatiTemplates() {
  const { data } = await adminApi.post("/admin/settings/wati/templates/refresh");
  return data;
}
export async function listWatiTemplates() {
  const { data } = await adminApi.get("/admin/settings/wati/templates");
  return data;
}

// Reminder templates (policy-premium reminder cron)
export async function fetchReminderTemplate() {
  const { data } = await adminApi.get("/admin/reminder-templates/policy_premium");
  return data;
}
export async function saveReminderTemplate(payload) {
  const { data } = await adminApi.put("/admin/reminder-templates/policy_premium", payload);
  return data;
}
export async function testSendReminder(payload) {
  const { data } = await adminApi.post(
    "/admin/reminder-templates/policy_premium/test-send",
    payload,
  );
  return data;
}

// Master taxonomy (asset & liability types)
export async function listAdminAssetTypes() {
  const { data } = await adminApi.get("/admin/asset-types");
  return data;
}
export async function createAdminAssetType(payload) {
  const { data } = await adminApi.post("/admin/asset-types", payload);
  return data;
}
export async function updateAdminAssetType(id, payload) {
  const { data } = await adminApi.put(`/admin/asset-types/${id}`, payload);
  return data;
}
export async function deleteAdminAssetType(id) {
  await adminApi.delete(`/admin/asset-types/${id}`);
}
export async function listAdminLiabilityTypes() {
  const { data } = await adminApi.get("/admin/liability-types");
  return data;
}
export async function createAdminLiabilityType(payload) {
  const { data } = await adminApi.post("/admin/liability-types", payload);
  return data;
}
export async function updateAdminLiabilityType(id, payload) {
  const { data } = await adminApi.put(`/admin/liability-types/${id}`, payload);
  return data;
}
export async function deleteAdminLiabilityType(id) {
  await adminApi.delete(`/admin/liability-types/${id}`);
}

// Contact requests — leads from the public landing page
export async function listContactRequests(statusFilter = "") {
  const { data } = await adminApi.get("/admin/requests", {
    params: statusFilter ? { status_filter: statusFilter } : {},
  });
  return data;
}

export async function updateContactRequestStatus(id, status) {
  const { data } = await adminApi.patch(`/admin/requests/${id}`, { status });
  return data;
}

export async function enrolFromContactRequest(id) {
  const { data } = await adminApi.post(`/admin/requests/${id}/enrol`);
  return data;
}

export async function deleteContactRequest(id) {
  await adminApi.delete(`/admin/requests/${id}`);
}

// Audit log — read surface only (writes happen automatically inside protected mutations).
export async function listAuditLog(filters = {}) {
  const { data } = await adminApi.get("/admin/audit-log", { params: filters });
  return data;
}
export async function listAuditLogActions() {
  const { data } = await adminApi.get("/admin/audit-log/actions");
  return data.actions;
}
export async function listAuditLogActors() {
  const { data } = await adminApi.get("/admin/audit-log/actors");
  return data.actors;
}
export function auditLogCsvUrl(filters = {}) {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => {
    if (v == null || v === "") return;
    if (Array.isArray(v)) v.forEach((x) => params.append(k, x));
    else params.set(k, String(v));
  });
  const qs = params.toString();
  return `${adminApi.defaults.baseURL}/admin/audit-log/csv${qs ? `?${qs}` : ""}`;
}

// Insurer KPIs — IRDAI data table used by the Health Policy Analyzer.
export async function adminListInsurerKpis() {
  const { data } = await adminApi.get("/admin/insurer-kpis");
  return data;
}

export async function adminSaveInsurerKpi(payload) {
  const { data } = await adminApi.post("/admin/insurer-kpis", payload);
  return data;
}

export async function adminUpdateInsurerKpi(id, payload) {
  const { data } = await adminApi.put(`/admin/insurer-kpis/${id}`, payload);
  return data;
}

export async function adminDeleteInsurerKpi(id) {
  await adminApi.delete(`/admin/insurer-kpis/${id}`);
}

export async function adminParseInsurerKpiUpload({ files, fiscalYear, onProgress }) {
  const form = new FormData();
  (Array.isArray(files) ? files : [files]).filter(Boolean).forEach((f) => form.append("files", f));
  if (fiscalYear) form.append("fiscal_year", fiscalYear);
  const { data } = await adminApi.post("/admin/insurer-kpis/parse", form, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: onProgress
      ? (e) => onProgress(e.total ? Math.round((e.loaded / e.total) * 100) : 0)
      : undefined,
    timeout: 240000,
  });
  return data;
}


// Budget categories (admin taxonomy)
export async function adminListBudgetCategories() {
  const { data } = await adminApi.get("/admin/budget-categories");
  return data;
}

export async function adminCreateBudgetCategory(payload) {
  const { data } = await adminApi.post("/admin/budget-categories", payload);
  return data;
}

export async function adminUpdateBudgetCategory(id, payload) {
  const { data } = await adminApi.put(`/admin/budget-categories/${id}`, payload);
  return data;
}

export async function adminDeleteBudgetCategory(id) {
  await adminApi.delete(`/admin/budget-categories/${id}`);
}

export async function adminSaveInsurerKpiBatch(payload) {
  const { data } = await adminApi.post("/admin/insurer-kpis/save", payload);
  return data;
}

